import { useEffect, useState } from 'react';
import { Checkbox } from '@/components/atoms/checkbox';
import {
  getScheduleNotificationOptions,
  ScheduleNotificationFields,
  type ScheduleNotificationChoice,
  type ScheduleNotificationOptions,
} from '@/hooks/jobs/useScheduleChangeNotification';

/** Shared by the legacy conflict modal and the v2 arrival-window modal. */
export function useMoveConfirmation(appointmentId?: string) {
  const [requested, setRequested] = useState(true);
  const [options, setOptions] = useState<ScheduleNotificationOptions | null>(null);
  const [choice, setChoice] = useState<ScheduleNotificationChoice>({});
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    let active = true;
    setOptions(null);
    setFailed(false);
    if (appointmentId)
      void getScheduleNotificationOptions(appointmentId, true)
        .then((value) => {
          if (!active) return;
          setOptions(value);
          setChoice({ email: Boolean(value.email), sms: Boolean(value.phone) });
        })
        .catch(() => {
          if (active) setFailed(true);
        });
    return () => {
      active = false;
    };
  }, [appointmentId, attempt]);
  return {
    choice: requested && options?.ask ? { ...choice, includeInitialConfirmation: true } : undefined,
    canConfirm:
      !appointmentId || !requested || Boolean(options?.ask && (choice.email || choice.sms)),
    fields: appointmentId ? (
      <div className="space-y-3">
        <label className="flex items-center gap-2 text-sm font-medium">
          <Checkbox
            checked={requested}
            onCheckedChange={(checked) => setRequested(checked === true)}
          />
          Request confirmation
        </label>
        {requested && (
          <>
            {!options && (
              <p className="text-xs text-muted-foreground">
                {failed ? (
                  <>
                    Could not check customer notifications.{' '}
                    <button
                      type="button"
                      className="underline"
                      onClick={() => setAttempt((value) => value + 1)}
                    >
                      Retry
                    </button>
                  </>
                ) : (
                  'Checking customer notification options…'
                )}
              </p>
            )}
            <ScheduleNotificationFields
              options={options}
              value={choice}
              onChange={setChoice}
              initialConfirmation
            />
            {options && (!options.ask || (!options.phone && !options.email)) && (
              <p className="text-xs text-muted-foreground">
                Confirmation is unavailable for this visit. Uncheck Request confirmation to move
                without sending.
              </p>
            )}
          </>
        )}
      </div>
    ) : null,
  };
}
